<template>
  <div class="app-extra-options" v-if="loadedContractState">
    <SimonModal
      v-if="hasRoamingOption"
      :is-visible="showRoamingModal"
      @hide="showRoamingModal = false"
      show-footer-buttons
      :btn-left-title="getSlotText('closeRoamingBlockModalButton', 'Abbrechen')"
      :btn-right-title="isRoamingActivatable ? getSlotText('enableRoamingBlockButton', 'Jetzt aktivieren') : getSlotText('disableRoamingBlockButton', 'Jetzt deaktivieren')"
      @btnLeft="showRoamingModal = false"
      @btnRight="handleRoamingModalSubmit()"
    >
      <template v-slot:modalTitle>
        <h2>
          <slot v-if="isRoamingActivatable"
                name="headingEnableRoamingBlockModal"></slot>
          <slot v-else-if="!isRoamingActivatable"
                name="headingDisableRoamingBlockModal"></slot>
        </h2>
      </template>
      <slot v-if="isRoamingActivatable"
            name="txtEnableRoamingBlockModal"></slot>
      <slot v-else-if="!isRoamingActivatable"
            name="txtDisableRoamingBlockModal"></slot>
    </SimonModal>

    <div v-if="!contractTerminated && !isSimCardLocked && !hasDunningLevel">
      <Filters @activeFilter="setActiveFilter"
               :filters="[getSlotText('allFilterExtraData'), getSlotText('dataHeadlineExtraData'), getSlotText('abroadHeadlineExtraData'), getSlotText('servicesHeadlineExtraData')]"/>

      <div class="tariff-options-wrapper dataOptions" v-if="showData && !showError && !loading">
        <h2 class="groupHeadline" v-if="activeFilter === getSlotText('allFilterExtraData')">
          <slot name="dataHeadlineExtraData">Headline</slot>
        </h2>
        <div class="tariff-options-column dataOption" v-for="option in dataTariffOptions" :key="option.tariffoptionId">
          <TariffOptionBox
            :option="option"
            :disabled="option.isAboOption ? option.isDisabledOptionBox : option.disabled"
            :bookable="option.isAboOption ? option.isBookableOptionBox : true"
            v-if="option.isBookableOption ? !option.conflictWithCurrent : true"
          >
            <template v-for="boxSlot in possibleChildSlotsFor('TariffOptionBox')" v-slot:[`${boxSlot.child}`]>
              <slot :name="`${boxSlot.parent}`"></slot>
            </template>
            <template v-for="slot in possibleChildSlotsFor('TariffOptions', option.tariffoptionId)"
                      v-slot:[`${slot.child}`]>
              <slot :id="option.tariffoptionId" :name="`${slot.parent}`"></slot>
            </template>
          </TariffOptionBox>
        </div>
      </div>

      <div class="tariff-options-wrapper roaming" v-if="showRoaming && !showError && !loading">
        <h2 class="groupHeadline" v-if="activeFilter === getSlotText('allFilterExtraData')">
          <slot name="abroadHeadlineExtraData">Headline</slot>
        </h2>
        <div class="tariff-options-column roamingOption" v-for="option in roamingTariffOptions"
             :key="option.tariffoptionId">
          <TariffOptionBox
            :option="option"
            :disabled="option.isAboOption ? option.isDisabledOptionBox : option.disabled"
            :bookable="option.isAboOption ? option.isBookableOptionBox : true"
            v-if="option.isBookableOption ? !option.conflictWithCurrent : true"
          >
            <template v-for="boxSlot in possibleChildSlotsFor('TariffOptionBox')" v-slot:[`${boxSlot.child}`]>
              <slot :name="`${boxSlot.parent}`"></slot>
            </template>
            <template v-for="slot in possibleChildSlotsFor('TariffOptions', option.tariffoptionId)"
                      v-slot:[`${slot.child}`]>
              <slot :id="option.tariffoptionId" :name="`${slot.parent}`"></slot>
            </template>
          </TariffOptionBox>
        </div>
      </div>

      <div class="tariff-options-wrapper services" v-if="showServices && !showError && !loading">
        <h2 class="groupHeadline" v-if="activeFilter === getSlotText('allFilterExtraData')">
          <slot name="servicesHeadlineExtraData">Headline</slot>
        </h2>
        <div class="tariff-options-column roaming"
             v-if="hasRoamingOption && (roamingOption.states.activatable || roamingOption.states.deactivatable)">
          <ServiceBox @submit="handleRoamingOption()"
                      :is-active="!isRoamingActivatable"
                      :is-btn-disabled="contractTerminated || contractDismissedInFuture || contractPauseActive">
            <template v-for="slot in possibleChildSlotsFor('RoamingOption')" v-slot:[`${slot.child}`]>
              <slot :name="`${slot.parent}`"></slot>
            </template>
            <template #btn>
              <slot v-if="isRoamingActivatable"
                    name="btnRoamingOption"></slot>
              <slot v-else-if="!isRoamingActivatable"
                    name="openRoamingUnblockModalButton"></slot>
            </template>
          </ServiceBox>
        </div>
        <div class="tariff-options-column anonym-block"
             v-if="hasAnonymBlockOption">
          <ServiceBox @submit="handleAnonymBlockSubmit()"
                      :is-active="isAnonymBlocked">
            <template v-for="slot in possibleChildSlotsFor('AnonymBlock')" v-slot:[`${slot.child}`]>
              <slot :name="`${slot.parent}`"></slot>
            </template>
            <template #btn>
              <slot v-if="!isAnonymBlocked"
                    name="btnAnonymBlock"></slot>
              <slot v-else-if="isAnonymBlocked"
                    name="btnDeactivateAnonymBlock"></slot>
            </template>
          </ServiceBox>
        </div>
        <div class="tariff-options-column mnp" v-if="showBelatedCta">
          <ServiceBox :is-btn-disabled="contractTerminated || contractDismissedInFuture"
                      @submit="handleMnpOption(getSlotText('btnLinkMnpOption'))">
            <template v-for="slot in possibleChildSlotsFor('MnpOption')" v-slot:[`${slot.child}`]>
              <slot :name="`${slot.parent}`"></slot>
            </template>
          </ServiceBox>
        </div>
      </div>
    </div>

    <div v-else class="info-box pastel">
      <slot :name="'txtNotBookable' + slotNameNoExtraOption">Uff! Hier ist ein Fehler aufgetreten</slot>
    </div>

    <div v-if="showError" v-html="errorMsg" class="error-box"></div>
  </div>
</template>

<script>
import { wrapQuery } from '@/helper/wrapGQLQuery'
import gql from 'graphql-tag'
import { splitError } from '@/helper/splitError'
import customerContractState from '@/mixins/customerContractState'
import { mapActions, mapGetters } from 'vuex'
import beErrors from '@/mixins/beErrors'
import Filters from '@/components/generic/Filters.vue'
import SimonModal from '@/components/modal/SimonModal.vue'
import ServiceBox from '@/components/process/generic/ExtraOptions/ServiceBox.vue'
import TariffOptionBox from '@/components/process/generic/Tariff/TariffOptionBox.vue'

export default {
  name: 'ExtraOptionsWrapper',
  components: {
    TariffOptionBox,
    ServiceBox,
    Filters,
    SimonModal
  },
  data () {
    return {
      tariffs: {
        bookedTariff: null
      },
      optionalServices: {
        optionalServices: []
      },
      serviceOptions: {
        anonymousCallRejection: false
      },
      currentCustomer: null,
      showError: false,
      loading: true,
      errorMsg: {},
      showRoamingModal: false,
      activeFilter: null,
      tariffoptions: {
        bookableTariffoptions: {
          bookableTariffoptions: []
        },
        bookedTariffoptions: {
          bookedTariffoptions: []
        },
        disabledTariffoptions: {
          disabledTariffoptions: []
        }
      }
    }
  },
  mixins: [
    customerContractState(),
    beErrors
  ],
  mounted () {
    this.startLoader()
  },
  computed: {
    ...mapGetters('checkout/tariff', [
      'checkKwk'
    ]),
    showBelatedCta: function () {
      return this.currentCustomer?.contract?.portingAllowed || false
    },
    kwkCustomer () {
      const bookedTariffId = this.tariffs?.bookedTariff?.tariffId
      if (this.checkKwk(bookedTariffId) === undefined) {
        return undefined
      }
      return this.checkKwk(bookedTariffId)
    },
    hasRoamingOption () {
      if (this.optionalServices.optionalServices && this.optionalServices.optionalServices.length > 0) {
        return this.roamingOption !== undefined
      }
      return false
    },
    hasAnonymBlockOption () {
      if (this.serviceOptions) {
        return this.serviceOptions.anonymousCallRejection !== undefined
      }
      return false
    },
    roamingOption () {
      return this.optionalServices.optionalServices.find(option => option.identifier === 'SperreRoam')
    },
    showServices () {
      return !!((this.showBelatedCta || this.hasRoamingOption) && (this.activeFilter === this.getSlotText('servicesHeadlineExtraData') || this.activeFilter === this.getSlotText('allFilterExtraData')))
    },
    showData () {
      const allFilter = this.getSlotText('allFilterExtraData')
      const dataHeadline = this.getSlotText('dataHeadlineExtraData')
      return (this.activeFilter === allFilter || this.activeFilter === dataHeadline) && this.dataTariffOptions.length > 0
    },
    showRoaming () {
      const allFilter = this.getSlotText('allFilterExtraData')
      const roamingHeadline = this.getSlotText('abroadHeadlineExtraData')
      return (this.activeFilter === allFilter || this.activeFilter === roamingHeadline) && this.roamingTariffOptions.length > 0
    },
    isRoamingActivatable () {
      if (!this.roamingOption.states.active && this.roamingOption.states.activatable) {
        return true
      } else if (this.roamingOption.states.active && this.roamingOption.states.deactivatable) {
        return false
      }
      return false
    },
    isAnonymBlocked () {
      return this.serviceOptions.anonymousCallRejection
    },
    dataTariffOptions () {
      const aboOptions = this.aboOptions.map(option => ({
        ...option,
        isAboOption: true
      }))
      return aboOptions.concat(this.bookableTariffoptions).filter(option => option.group === 'data')
    },
    roamingTariffOptions () {
      const bookableOptions = this.bookableTariffoptions.map(option => ({
        ...option,
        isBookableOption: true
      }))
      return bookableOptions.concat(this.dataTariffOptions).filter(option => option.group === 'roaming')
    },
    bookableTariffoptions: function () { // only tariff options, not abo options
      return this.tariffoptions.bookableTariffoptions.bookableTariffoptions.map(tariffOption => {
        tariffOption.disabled = this.contractTerminated || this.contractPauseActive
        tariffOption.disabledInfo = ''
        if (tariffOption.disabled) {
          tariffOption.disabledInfo = this.$slots.pausedTariffInfoTxt[0].text
        }
        return tariffOption
      }).filter(
        tariffOption => tariffOption.group
      ).filter(
        tariffOption => !tariffOption.automaticExtension
      ).sort((a, b) => parseInt(a.tariffoptionId) - parseInt(b.tariffoptionId))
    },
    aboOptions: function () { // maps booked, bookable & disabled abo options
      let bookedOrBookable = this.tariffoptions.bookedTariffoptions.bookedTariffoptions
      bookedOrBookable = bookedOrBookable.concat(this.tariffoptions.bookableTariffoptions.bookableTariffoptions)
      bookedOrBookable.forEach(option => {
        option.disabled = this.contractTerminated || this.contractPauseActive
        option.disabledInfo = ''
        if (option.disabled) {
          option.disabledInfo = this.$slots.pausedTariffInfoTxt[0].text
        }
        option.isDisabledOptionBox = option.disabled
        option.isBookableOptionBox = true
      })
      const disabled = this.tariffoptions.disabledTariffoptions.disabledTariffoptions
      disabled.forEach(option => {
        option.isDisabledOptionBox = false
        option.isBookableOptionBox = false
      })
      return bookedOrBookable.concat(
        disabled
      ).filter(
        tariffOption => tariffOption.automaticExtension
      ).filter(
        tariffOption => tariffOption.group
      ).sort((a, b) => {
        // sort by volume amount
        const volumeA = a.volumes?.[0]?.amount ?? 0
        const volumeB = b.volumes?.[0]?.amount ?? 0
        if (volumeA !== volumeB) {
          return volumeA - volumeB // Sort by ascending volume amount
        }
        return parseInt(a.tariffoptionId) - parseInt(b.tariffoptionId)
      })
    },
    slotNameNoExtraOption () {
      if (this.hasDunningLevel) {
        return 'DunningLevelTooHigh'
      }
      if (this.contractTerminated) {
        return 'ContractTerminated'
      }
      if (this.isSimCardLocked) {
        return 'ContractPassivated'
      }
      return ''
    }
  },
  methods: {
    ...mapActions('tariffOptions', ['addOptionalService', 'removeOptionalService', 'changeServiceOptions']),
    handleMnpOption (link) {
      window.location = link
    },
    handleRoamingOption () {
      this.showRoamingModal = true
    },
    handleRoamingModalSubmit () {
      if (!this.roamingOption.states.active && this.roamingOption.states.activatable) {
        this.activateServiceOption('SperreRoam')
      } else if (this.roamingOption.states.active && this.roamingOption.states.deactivatable) {
        this.deactivateServiceOption('SperreRoam')
      }
    },
    handleAnonymBlockSubmit () {
      this.changeAnonymBlock(!this.serviceOptions.anonymousCallRejection)
    },
    activateServiceOption (identifier) {
      this.startLoader()
      this.addOptionalService({ serviceOptionId: identifier })
        .then(response => {
          if (response) {
            this.$apollo.queries.serviceOptions.refetch().then(() => {
              this.showRoamingModal = false
              this.successToast(this.getSlotText('enableRoamingBlockSuccess') || 'success enable')
              this.stopLoader()
            })
          } else {
            this.stopLoader()
            this.showRoamingModal = false
            const errMessage = this.beErrorGetLastMessage()
            this.errorToast(errMessage)
          }
        })
        .catch(() => {
          this.stopLoader()
          this.showRoamingModal = false
          const errMessage = this.beErrorGetLastMessage()
          this.errorToast(errMessage)
        })
    },
    deactivateServiceOption (identifier) {
      this.startLoader()

      this.removeOptionalService({ serviceOptionId: identifier })
        .then(response => {
          if (response) {
            this.$apollo.queries.serviceOptions.refetch().then(() => {
              this.showRoamingModal = false
              this.successToast(this.getSlotText('disableRoamingBlockSuccess') || 'success disable')
              this.stopLoader()
            })
          } else {
            this.stopLoader()
            this.showRoamingModal = false
            const errMessage = this.beErrorGetLastMessage()
            this.errorToast(errMessage)
          }
        })
        .catch(() => {
          this.stopLoader()
          this.showRoamingModal = false
          const errMessage = this.beErrorGetLastMessage()
          this.errorToast(errMessage)
        })
    },
    changeAnonymBlock (anonymousCallRejection) {
      this.startLoader()

      this.changeServiceOptions({ anonymousCallRejection })
        .then(response => {
          if (response) {
            this.successToast(
              this.getSlotText(
                anonymousCallRejection ? 'txtToastActivatedSuccessAnonymBlock' : 'txtToastDeactivatedSuccessAnonymBlock'
              ) || (anonymousCallRejection ? 'success enable' : 'success disable')
            )
          } else {
            const errMessage = this.beErrorGetLastMessage()
            this.errorToast(errMessage)
          }
          this.stopLoader()
        })
        .catch(() => {
          const errMessage = this.beErrorGetLastMessage()
          this.errorToast(errMessage)
          this.stopLoader()
        })
    },
    setActiveFilter (filter) {
      this.activeFilter = filter
    }
  },
  apollo: {
    serviceOptions: function () {
      return wrapQuery(
        gql`query serviceOptions {
          tariffs {
            bookedTariff {
              tariffId
              tariffState
              name
              basicFee formattedBasicFee
            }
          }
          optionalServices {
            optionalServices {
              identifier
              name
              states {
                activatable
                active
                deactivatable
              }
            }
          }
          serviceOptions {
            anonymousCallRejection
          }
        }`, this, ['tariffs', 'optionalServices', 'serviceOptions'], {}, () => {
          const _this = this
          _this.showError = false
          _this.loading = false
          this.stopLoader()
        },
        {},
        (error) => {
          splitError(error).forEach(e => {
            this.errorMsg = e.message
          })
          if (!this.showError) {
            const data = {
              type: 'technical',
              message: this.errorMsg
            }
            if (typeof window.trackingNS?.track?.tracking === 'function') {
              window.trackingNS.track.tracking('error', data)
            }
          }
          this.showError = true
        }
      )
    },
    tariffoptions: function () {
      return wrapQuery(
        gql`query tariffOptions {
           tariffoptions {
            bookedTariffoptions {
              bookedTariffoptions {
                automaticExtension
                tariffoptionId
                name
                details
                price
                formattedPrice
                statusKey
                revocable
                group
                activeRuntime {
                  endOfRuntime
                  startOfRuntime
                }
                duration {
                  amount
                  unit
                  formattedUnit
                }
                volumes {
                  formattedUnit
                  type
                  amount
                  unit
                }
                expirationDate
              }
            }
            bookableTariffoptions {
              bookableTariffoptions {
                automaticExtension
                conflictWithCurrent
                tariffoptionId
                price
                formattedPrice
                name
                details
                group
                duration {
                  amount
                  unit
                  formattedUnit
                }
                volumes {
                  formattedUnit
                  type
                  amount
                  unit
                }
              }
            }
              disabledTariffoptions {
                disabledTariffoptions {
                    automaticExtension
                    conflictWithCurrent
                    tariffoptionId
                    price
                    formattedPrice
                    name
                    details
                    group
                    duration {
                      amount
                      unit
                      formattedUnit
                    }
                    volumes {
                      formattedUnit
                      type
                      amount
                      unit
                    }
                }
            }
          }
        }`, this, 'tariffoptions', {}, () => {
          this.loading = false
          this.showError = false
          this.errorMsg = ''
        },
        {
          fetchPolicy: 'cache-first'
        },
        (error) => {
          this.loading = false
          this.errorMsg = this.beErrorGetLastMessage()
          if (this.errorMsg === '') {
            splitError(error).forEach(e => {
              this.errorMsg = e.message
            })
          }
          this.showError = true
        }
      )
    },
    currentCustomer: function () {
      return wrapQuery(
        gql`query currentCustomer {
           currentCustomer {
             contract {
               portingAllowed
             }
           }
        }`, this, 'currentCustomer', () => {
        }
      )
    }
  }
}
</script>
